import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gift, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';
import { captureBookingSource } from '../utils/bookingSource';

/** Referral landing page, /r/{code}
 * Sets a 30-day cookie so the referral auto-applies at checkout.
 * Shows a celebratory welcome, then redirects to /discover.
 */
export default function ReferralLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [referrerName, setReferrerName] = useState(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!code) return;
    // Anchor the session source to "referral" so booking creation
    // attributes correctly. Synthetic ?ref=<code> query matches the
    // captureBookingSource contract.
    captureBookingSource({ pathname: `/r/${code}`, search: `?ref=${code}` });
    api.get(`/referrals/resolve/${encodeURIComponent(code)}`)
      .then(res => {
        setReferrerName(res.data.referrer_first_name);
        // Keep the legacy 30-day cookie for the first-booking discount
        // window, this is separate from session-source attribution.
        document.cookie = `zuriya_referral=${code}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
        toast.success(`£5 off your first Zuriya booking, thanks to ${res.data.referrer_first_name}!`);
      })
      .catch(() => setInvalid(true));
  }, [code]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 bg-gradient-to-br from-[#FDF8EA] via-[#F9F6F0] to-white">
      <div className="max-w-lg w-full text-center" data-testid="referral-landing">
        {invalid ? (
          <>
            <div className="w-16 h-16 rounded-full bg-stone-100 mx-auto mb-6 flex items-center justify-center">
              <Gift className="h-8 w-8 text-stone-400" />
            </div>
            <h1 className="font-heading text-3xl text-[#1A1A1A] mb-3">Link expired or not found</h1>
            <p className="text-stone-500 mb-8">Ask your friend for a fresh link, or head over to Discover to find a stylist.</p>
            <button
              onClick={() => navigate('/discover')}
              className="btn-primary px-8 py-3 rounded-full"
              data-testid="referral-go-discover"
            >
              Browse stylists
            </button>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F5D56A] to-[#D4AF37] mx-auto mb-6 flex items-center justify-center shadow-lg shadow-amber-200">
              <Sparkles className="h-10 w-10 text-white" />
            </div>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[#D4AF37] mb-3">
              You've been invited
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A] mb-4 leading-tight">
              {referrerName ? `${referrerName} gave you £5 off` : 'You have £5 off'}
            </h1>
            <p className="text-stone-600 text-lg mb-8 leading-relaxed">
              Book any Zuriya service and we'll take <span className="font-semibold text-[#1A1A1A]">£5 off</span> your first appointment automatically at checkout.
            </p>
            <button
              onClick={() => navigate('/discover')}
              className="btn-primary px-10 py-4 rounded-full text-base font-semibold"
              data-testid="referral-claim-btn"
            >
              Find my stylist
            </button>
            <p className="text-xs text-stone-400 mt-6">
              New clients only. Discount applies to first Zuriya booking within 30 days.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
